import { useCallback } from 'react'

import {
  startRegistration,
  browserSupportsWebAuthn,
  platformAuthenticatorIsAvailable,
  PublicKeyCredentialCreationOptionsJSON,
} from '@simplewebauthn/browser'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { QUERY_KEYS } from '~/constants/react-query'
import { useActiveOrgUpdates } from '~/hooks/org/useActiveOrgUpdates'
import { useServerRequests } from '~/hooks/useRequests'
import { BackendPasskey, OrgSigner } from '~/types'
import { captureException, captureMessage } from '~/utils/errors'
import { sendTrackingData } from '~/utils/tracking'

export const useAddNewPasskey = () => {
  const { serverGetRequest } = useServerRequests()
  const { serverPostRequest } = useServerRequests()
  const { addPasskey } = useActiveOrgUpdates()
  const queryClient = useQueryClient()

  const verifyRegistration = useCallback(
    async (registrationInfo: string): Promise<OrgSigner | undefined> => {
      const verificationResponse = await serverPostRequest<{
        verified: boolean
        credentialData: BackendPasskey
      }>({
        path: '/api/passkey/register/verify',
        params: {
          registration: registrationInfo,
        },
        errorMessage: 'Failed to verify passkey',
      })

      if (verificationResponse?.data.verified) {
        toast.success('Passkey created')
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.AUTH_USER],
        })
        addPasskey(verificationResponse.data.credentialData)
        sendTrackingData('addedPasskey')

        // Return the newly created passkey as OrgSigner
        return {
          ...verificationResponse.data.credentialData,
          isArchived: false,
        }
      } else {
        toast.error('Failed to add passkey', {
          description: 'Please try again',
        })
        return undefined
      }
    },
    [queryClient, serverPostRequest, addPasskey],
  )

  const addNewPasskey = async (): Promise<OrgSigner | undefined> => {
    if (
      !browserSupportsWebAuthn() ||
      !(await platformAuthenticatorIsAvailable())
    ) {
      toast.error('Browser does not support passkeys', {
        description: 'Use a different browser or device to sign with a passkey',
      })
      return undefined
    }

    const registrationOptionsResponse = await serverGetRequest<{
      options: PublicKeyCredentialCreationOptionsJSON
    }>({
      path: '/api/passkey/register/options',
    })
    if (!registrationOptionsResponse) {
      toast.error('Failed to add new passkey', {
        description: 'Please try again',
      })
      return undefined
    }

    let attResp

    try {
      // Pass the options to the authenticator and wait for a response
      attResp = await startRegistration({
        optionsJSON: registrationOptionsResponse.data.options,
      })

      return await verifyRegistration(JSON.stringify(attResp))
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error)
      const errorName = error instanceof Error ? error.name : 'unknown'

      if (error.name === 'NotAllowedError') {
        // On iOS, NotAllowedError can wrap credential provider failures
        // (e.g. 1Password errors), not just user cancellations.
        const isLikelyCancellation =
          !errorMessage || errorMessage.includes('timed out or was not allowed')

        if (!isLikelyCancellation) {
          captureMessage(`Passkey registration NotAllowedError`, {
            tags: {
              feature: 'passkey',
              operation: 'registration',
            },
            extra: { errorMessage },
          })
          toast.error('Passkey registration failed', {
            description:
              'Your device or password manager could not complete the request. Please try again or use a different method.',
          })
        }
        return undefined
      } else if (error.code === 'ERROR_AUTHENTICATOR_PREVIOUSLY_REGISTERED') {
        toast.error('Passkey provider already in use', {
          description: 'Please select a different one',
        })
      } else {
        toast.error('Failed to add new passkey', {
          description: errorMessage || 'Please try again',
        })
      }

      captureException(error, {
        tags: {
          feature: 'passkey',
          operation: 'registration',
          errorType: errorName,
        },
        extra: {
          errorMessage,
        },
      })
      return undefined
    }
  }

  return { addNewPasskey }
}
